<template>
  <AppHeaderBack title="Update Personal Information" />
  <div class="min-h-screen bg-blue-50">
    <!-- Header section with UP colors -->
    <div class="bg-gradient-to-r from-blue-900 to-blue-700 py-8 shadow-md">
      <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div class="flex flex-col items-center">
          <div class="text-center">
            <h1 class="text-2xl font-bold text-white mb-1">Update Personal Information</h1>
            <p class="text-blue-100">Keep your profile up to date</p>
          </div>
        </div>
      </div>
    </div>

    <!-- Main content -->
    <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
      <div class="grid grid-cols-1 max-w-xl mx-auto">
        <div class="bg-white rounded-lg shadow transform transition-transform duration-300 hover:-translate-y-1 hover:shadow-md">
          <div class="p-6">
            <form @submit.prevent="updateProfile" class="space-y-6">
              <!-- Name Fields -->
              <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label for="name" class="block text-sm font-medium text-gray-700 mb-1">First Name</label>
                  <div class="relative">
                    <div class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <span class="material-symbols-outlined text-gray-400">person</span>
                    </div>
                    <input
                      id="name"
                      v-model="name"
                      type="text"
                      placeholder="Your first name"
                      required
                      :disabled="loading"
                      class="block w-full pl-10 pr-3 py-3 border border-gray-300 rounded-lg shadow-sm focus:ring-2 focus:ring-blue-900 focus:border-blue-900 placeholder-gray-400 transition duration-200 disabled:opacity-50"
                    />
                  </div>
                </div>
                <div>
                  <label for="surname" class="block text-sm font-medium text-gray-700 mb-1">Last Name</label>
                  <div class="relative">
                    <div class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <span class="material-symbols-outlined text-gray-400">badge</span>
                    </div>
                    <input
                      id="surname"
                      v-model="surname"
                      type="text"
                      placeholder="Your last name"
                      required
                      :disabled="loading"
                      class="block w-full pl-10 pr-3 py-3 border border-gray-300 rounded-lg shadow-sm focus:ring-2 focus:ring-blue-900 focus:border-blue-900 placeholder-gray-400 transition duration-200 disabled:opacity-50"
                    />
                  </div>
                </div>
              </div>

              <!-- Email Field -->
              <div>
                <label for="email" class="block text-sm font-medium text-gray-700 mb-1">Email Address</label>
                <div class="relative">
                  <div class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <span class="material-symbols-outlined text-gray-400">mail</span>
                  </div>
                  <input
                    id="email"
                    v-model="email"
                    type="email"
                    placeholder="your@email.com"
                    required
                    :disabled="true"
                    class="block w-full pl-10 pr-3 py-3 border border-gray-300 rounded-lg bg-gray-100 cursor-not-allowed text-gray-700"
                  />
                </div>
                <p class="mt-1 text-xs text-gray-500">Contact support to change your email address</p>
              </div>

              <!-- Submit Button -->
              <button
                type="submit"
                :disabled="loading"
                class="w-full flex justify-center items-center py-3 px-4 rounded-lg font-semibold text-white bg-gradient-to-r from-blue-900 to-blue-700 hover:from-blue-800 hover:to-blue-600 transition disabled:opacity-60"
              >
                <svg v-if="loading" class="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                  <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                {{ loading ? "Updating..." : "Save Changes" }}
              </button>

              <!-- Cancel Button -->
              <router-link 
                to="/profile"
                class="w-full flex justify-center items-center py-3 px-4 rounded-lg font-semibold text-blue-900 border border-blue-900 bg-white hover:bg-blue-50 transition"
              >
                Cancel
              </router-link>
            </form>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import AppHeaderBack from '@/components/common/AppHeaderBack.vue';
import { mapState, mapMutations } from 'vuex';
import { updateUserProfile, getUserProfile } from '@/composables/useFirestore';

export default {
  components:{
    AppHeaderBack,
  },
  data() {
    return {
      name: '',
      surname: '',
      email: ''
    };
  },
  computed: {
    ...mapState(['user', 'profile', 'loading']),
  },
  watch: {
    profile: {
      handler(newProfile) {
        if (newProfile) {
          this.name = newProfile.name || '';
          this.surname = newProfile.surname || '';
          this.email = newProfile.email || '';
        }
      },
      immediate: true
    }
  },
  methods: {
    ...mapMutations(['setLoading', 'setProfile']),
    async updateProfile() {
      if (!this.name || !this.surname) {
        this.$store.dispatch('showAlert', { 
          message: "Please enter both first and last name", 
          type: "error" 
        });
        return;
      }

      this.setLoading(true);
      try {
        const updateData = {
          name: this.name,
          surname: this.surname
        };

        await updateUserProfile(this.user.uid, updateData);
        const updatedProfile = await getUserProfile(this.user.uid);
        this.setProfile(updatedProfile);

        this.$store.dispatch('showAlert', { 
          message: "Personal information updated successfully!", 
          type: "success" 
        });
        this.$router.push('/profile');
      } catch (error) {
        this.$store.dispatch('showAlert', { 
          message: error.message || "Failed to update profile", 
          type: "error" 
        });
      } finally {
        this.setLoading(false);
      }
    }
  }
};
</script>

<!-- No custom CSS needed, all styling is handled by Tailwind classes -->